<template>
  <div class="report-xiaoshan" ref="imageDom">
    <!-- 打印页眉：position:fixed 使其在每页顶部重复出现 -->
    <div class="xs-print-header">
      <p
        v-for="(item, index) in model.reportTitleArray"
        :key="index"
        class="xs-print-header-title"
      >{{ item }}</p>
      <p class="xs-print-header-subtitle">{{ model.reportSubtitle }}</p>
    </div>

    <div class="report-xiaoshan-container">
      <!-- 屏幕上显示完整标题栏，打印时隐藏 -->
      <div class="xs-screen-only">
        <report-title
          :model="model"
          @onDownloadPDFClick="onDownloadPDFClick"
          @onDownloadPNGClick="onDownloadPNGClick"
          @onPrintClick="onPrintClick"
          @onShareClick="onShareClick"
        ></report-title>
      </div>

      <!-- 个人资料 -->
      <table class="xs-table">
        <thead>
          <tr><th colspan="8" class="xs-section-header">个人资料</th></tr>
        </thead>
        <tbody>
          <tr>
            <td class="xs-label">姓名</td>
            <td>{{ fmt(model.initialPatientName, false) }}</td>
            <td class="xs-label">性别</td>
            <td>{{ fmt(model.initialPatientGender, false) }}</td>
            <td class="xs-label">年龄</td>
            <td>{{ fmt(model.initialPatientAge, false) }}</td>
            <td class="xs-label">出生日期</td>
            <td>{{ fmt(birthday, false) }}</td>
          </tr>
          <tr>
            <td class="xs-label">身高</td>
            <td>{{ fmtUnit(model.initialPatientHeight, 'cm') }}</td>
            <td class="xs-label">体重</td>
            <td>{{ fmtUnit(model.initialPatientWeight, 'kg') }}</td>
            <td class="xs-label">BMI</td>
            <td>{{ fmt(bmi) }}</td>
            <td class="xs-label">科别</td>
            <td>{{ fmt(model.initialPatientDepartment, false) }}</td>
          </tr>
          <tr>
            <td class="xs-label">睡前血压</td>
            <td>{{ bloodPressureStr }}mmHg</td>
            <td class="xs-label">醒后血压</td>
            <td>--/--mmHg</td>
            <td class="xs-label">腰围</td>
            <td>{{ fmt(waistCircumference, false) }}</td>
            <td class="xs-label">颈围</td>
            <td>{{ fmt(neckCircumference, false) }}</td>
          </tr>
          <tr>
            <td class="xs-label">病历号</td>
            <td colspan="3">{{ fmt(model.initialPatientDiagnoseNumber, false) }}</td>
            <td class="xs-label">电话</td>
            <td colspan="3">{{ fmt(mobile, false) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- 睡眠结构分析 -->
      <table class="xs-table">
        <thead>
          <tr><th colspan="2" class="xs-section-header">睡眠结构分析</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>关灯时间(hh:mm:ss)：</td>
            <td>{{ lightoffTimeStr }}</td>
          </tr>
          <tr>
            <td>开灯时间(hh:mm:ss)：</td>
            <td>{{ lightonTimeStr }}</td>
          </tr>
          <tr>
            <td>总记录时间(TRT; hh:mm:ss)：</td>
            <td>{{ trtStr }}</td>
          </tr>
          <tr>
            <td>总睡眠时间(TST; hh:mm:ss)：</td>
            <td>{{ tstStr }}</td>
          </tr>
        </tbody>
      </table>

      <!-- 呼吸暂停事件 -->
      <table class="xs-table">
        <thead>
          <tr><th colspan="5" class="xs-section-header">呼吸暂停事件</th></tr>
        </thead>
        <tbody>
          <tr class="xs-col-header">
            <td>参数</td>
            <td>阻塞性</td>
            <td>中枢性</td>
            <td>混合性</td>
            <td>总呼吸暂停</td>
          </tr>
          <tr>
            <td>平均长度 (秒)</td>
            <td>--</td>
            <td>--</td>
            <td>--</td>
            <td>{{ fmt(stat.beMeanLen) }}</td>
          </tr>
          <tr>
            <td>最长事件 (秒)</td>
            <td>--</td>
            <td>--</td>
            <td>--</td>
            <td>{{ fmt(longestTime) }}</td>
          </tr>
          <tr>
            <td>TST 次数</td>
            <td>--</td>
            <td>{{ fmtInt(stat.becCnt) }}</td>
            <td>{{ fmtInt(stat.bemCnt) }}</td>
            <td>--</td>
          </tr>
          <tr>
            <td>TST 指数</td>
            <td>--</td>
            <td>{{ fmtIndex(stat.becCnt) }}</td>
            <td>{{ fmtIndex(stat.bemCnt) }}</td>
            <td>--</td>
          </tr>
        </tbody>
      </table>

      <!-- 呼吸事件 -->
      <table class="xs-table">
        <thead>
          <tr>
            <th colspan="5" class="xs-section-header">
              呼吸事件（鼾声次数：{{ snoreCntStr }}  鼾声指数：{{ snoreIndexStr }}）
            </th>
          </tr>
        </thead>
        <tbody>
          <tr class="xs-col-header">
            <td>参数</td>
            <td>总呼吸暂停</td>
            <td>低通气</td>
            <td>陈施式呼吸</td>
            <td>合计</td>
          </tr>
          <tr>
            <td>平均长度 (秒)</td>
            <td>--</td>
            <td>--</td>
            <td>--</td>
            <td>{{ fmt(stat.beMeanLen) }}</td>
          </tr>
          <tr>
            <td>最长事件 (秒)</td>
            <td>--</td>
            <td>--</td>
            <td>--</td>
            <td>{{ fmt(stat.beMaxLen) }}</td>
          </tr>
          <tr>
            <td>TST 次数</td>
            <td>--</td>
            <td>--</td>
            <td>--</td>
            <td>--</td>
          </tr>
          <tr>
            <td>TST 指数</td>
            <td>--</td>
            <td>--</td>
            <td>--</td>
            <td>--</td>
          </tr>
          <tr>
            <td>AHI (次/小时)</td>
            <td colspan="2" style="text-align:center;">{{ fmtTwo(model.ahi) }}</td>
            <td>--</td>
            <td>--</td>
          </tr>
        </tbody>
      </table>

      <!-- 呼吸事件（与体位相关性） -->
      <table class="xs-table">
        <thead>
          <tr><th colspan="9" class="xs-section-header">呼吸事件（与体位相关性）</th></tr>
          <tr class="xs-col-header">
            <td rowspan="2">呼吸事件</td>
            <td colspan="2">仰卧</td>
            <td colspan="2">俯卧</td>
            <td colspan="2">左侧卧</td>
            <td colspan="2">右侧卧</td>
          </tr>
          <tr class="xs-col-header">
            <td>次数</td><td>指数</td>
            <td>次数</td><td>指数</td>
            <td>次数</td><td>指数</td>
            <td>次数</td><td>指数</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>累计时间(hh:mm:ss)</td>
            <td colspan="2">{{ stat.supineTime != null ? secondsToHMS(stat.supineTime) : '--' }}</td>
            <td colspan="2">{{ stat.proneTime != null ? secondsToHMS(stat.proneTime) : '--' }}</td>
            <td colspan="2">{{ stat.leftLateralTime != null ? secondsToHMS(stat.leftLateralTime) : '--' }}</td>
            <td colspan="2">{{ stat.rightLateralTime != null ? secondsToHMS(stat.rightLateralTime) : '--' }}</td>
          </tr>
          <tr>
            <td>阻塞型</td>
            <td>{{ fmtInt(stat.supineBeoCnt) }}</td>
            <td>{{ fmtPosIndex(stat.supineBeoCnt, stat.supineTime) }}</td>
            <td>{{ fmtInt(stat.proneBeoCnt) }}</td>
            <td>{{ fmtPosIndex(stat.proneBeoCnt, stat.proneTime) }}</td>
            <td>{{ fmtInt(stat.leftLateralBeoCnt) }}</td>
            <td>{{ fmtPosIndex(stat.leftLateralBeoCnt, stat.leftLateralTime) }}</td>
            <td>{{ fmtInt(stat.rightLateralBeoCnt) }}</td>
            <td>{{ fmtPosIndex(stat.rightLateralBeoCnt, stat.rightLateralTime) }}</td>
          </tr>
          <tr>
            <td>中枢型</td>
            <td>{{ fmtInt(stat.supineBecCnt) }}</td>
            <td>{{ fmtPosIndex(stat.supineBecCnt, stat.supineTime) }}</td>
            <td>{{ fmtInt(stat.proneBecCnt) }}</td>
            <td>{{ fmtPosIndex(stat.proneBecCnt, stat.proneTime) }}</td>
            <td>{{ fmtInt(stat.leftLateralBecCnt) }}</td>
            <td>{{ fmtPosIndex(stat.leftLateralBecCnt, stat.leftLateralTime) }}</td>
            <td>{{ fmtInt(stat.rightLateralBecCnt) }}</td>
            <td>{{ fmtPosIndex(stat.rightLateralBecCnt, stat.rightLateralTime) }}</td>
          </tr>
          <tr>
            <td>混合型</td>
            <td>{{ fmtInt(stat.supineBemCnt) }}</td>
            <td>{{ fmtPosIndex(stat.supineBemCnt, stat.supineTime) }}</td>
            <td>{{ fmtInt(stat.proneBemCnt) }}</td>
            <td>{{ fmtPosIndex(stat.proneBemCnt, stat.proneTime) }}</td>
            <td>{{ fmtInt(stat.leftLateralBemCnt) }}</td>
            <td>{{ fmtPosIndex(stat.leftLateralBemCnt, stat.leftLateralTime) }}</td>
            <td>{{ fmtInt(stat.rightLateralBemCnt) }}</td>
            <td>{{ fmtPosIndex(stat.rightLateralBemCnt, stat.rightLateralTime) }}</td>
          </tr>
          <tr>
            <td>总呼吸暂停</td>
            <td>{{ fmtInt(posTotalApnea(stat.supineBeoCnt, stat.supineBecCnt, stat.supineBemCnt)) }}</td>
            <td>{{ fmtPosIndex(posTotalApnea(stat.supineBeoCnt, stat.supineBecCnt, stat.supineBemCnt), stat.supineTime) }}</td>
            <td>{{ fmtInt(posTotalApnea(stat.proneBeoCnt, stat.proneBecCnt, stat.proneBemCnt)) }}</td>
            <td>{{ fmtPosIndex(posTotalApnea(stat.proneBeoCnt, stat.proneBecCnt, stat.proneBemCnt), stat.proneTime) }}</td>
            <td>{{ fmtInt(posTotalApnea(stat.leftLateralBeoCnt, stat.leftLateralBecCnt, stat.leftLateralBemCnt)) }}</td>
            <td>{{ fmtPosIndex(posTotalApnea(stat.leftLateralBeoCnt, stat.leftLateralBecCnt, stat.leftLateralBemCnt), stat.leftLateralTime) }}</td>
            <td>{{ fmtInt(posTotalApnea(stat.rightLateralBeoCnt, stat.rightLateralBecCnt, stat.rightLateralBemCnt)) }}</td>
            <td>{{ fmtPosIndex(posTotalApnea(stat.rightLateralBeoCnt, stat.rightLateralBecCnt, stat.rightLateralBemCnt), stat.rightLateralTime) }}</td>
          </tr>
          <tr>
            <td>低通气</td>
            <td>{{ fmtInt(stat.supineBehCnt) }}</td>
            <td>{{ fmtPosIndex(stat.supineBehCnt, stat.supineTime) }}</td>
            <td>{{ fmtInt(stat.proneBehCnt) }}</td>
            <td>{{ fmtPosIndex(stat.proneBehCnt, stat.proneTime) }}</td>
            <td>{{ fmtInt(stat.leftLateralBehCnt) }}</td>
            <td>{{ fmtPosIndex(stat.leftLateralBehCnt, stat.leftLateralTime) }}</td>
            <td>{{ fmtInt(stat.rightLateralBehCnt) }}</td>
            <td>{{ fmtPosIndex(stat.rightLateralBehCnt, stat.rightLateralTime) }}</td>
          </tr>
          <tr>
            <td>陈施式呼吸</td>
            <td>{{ fmtInt(stat.supineBeCsCnt) }}</td>
            <td>{{ fmtPosIndex(stat.supineBeCsCnt, stat.supineTime) }}</td>
            <td>{{ fmtInt(stat.proneBeCsCnt) }}</td>
            <td>{{ fmtPosIndex(stat.proneBeCsCnt, stat.proneTime) }}</td>
            <td>{{ fmtInt(stat.leftLateralBeCsCnt) }}</td>
            <td>{{ fmtPosIndex(stat.leftLateralBeCsCnt, stat.leftLateralTime) }}</td>
            <td>{{ fmtInt(stat.rightLateralBeCsCnt) }}</td>
            <td>{{ fmtPosIndex(stat.rightLateralBeCsCnt, stat.rightLateralTime) }}</td>
          </tr>
          <tr>
            <td>合计</td>
            <td>{{ fmtInt(posTotal(stat.supineBeoCnt, stat.supineBecCnt, stat.supineBemCnt, stat.supineBehCnt, stat.supineBeCsCnt)) }}</td>
            <td>{{ fmtPosIndex(posTotal(stat.supineBeoCnt, stat.supineBecCnt, stat.supineBemCnt, stat.supineBehCnt, stat.supineBeCsCnt), stat.supineTime) }}</td>
            <td>{{ fmtInt(posTotal(stat.proneBeoCnt, stat.proneBecCnt, stat.proneBemCnt, stat.proneBehCnt, stat.proneBeCsCnt)) }}</td>
            <td>{{ fmtPosIndex(posTotal(stat.proneBeoCnt, stat.proneBecCnt, stat.proneBemCnt, stat.proneBehCnt, stat.proneBeCsCnt), stat.proneTime) }}</td>
            <td>{{ fmtInt(posTotal(stat.leftLateralBeoCnt, stat.leftLateralBecCnt, stat.leftLateralBemCnt, stat.leftLateralBehCnt, stat.leftLateralBeCsCnt)) }}</td>
            <td>{{ fmtPosIndex(posTotal(stat.leftLateralBeoCnt, stat.leftLateralBecCnt, stat.leftLateralBemCnt, stat.leftLateralBehCnt, stat.leftLateralBeCsCnt), stat.leftLateralTime) }}</td>
            <td>{{ fmtInt(posTotal(stat.rightLateralBeoCnt, stat.rightLateralBecCnt, stat.rightLateralBemCnt, stat.rightLateralBehCnt, stat.rightLateralBeCsCnt)) }}</td>
            <td>{{ fmtPosIndex(posTotal(stat.rightLateralBeoCnt, stat.rightLateralBecCnt, stat.rightLateralBemCnt, stat.rightLateralBehCnt, stat.rightLateralBeCsCnt), stat.rightLateralTime) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- 氧减事件 -->
      <table class="xs-table">
        <thead>
          <tr><th colspan="3" class="xs-section-header">氧减事件（血氧饱和度下降≥3%）</th></tr>
          <tr class="xs-col-header">
            <td>参数</td>
            <td>次数</td>
            <td>指数</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>W期</td>
            <td>{{ fmtInt(stat.wOdiCnt) }}</td>
            <td>{{ fmt(stat.wOdiIdx) }}</td>
          </tr>
          <tr>
            <td>TST</td>
            <td>{{ fmtInt(stat.tstOdiCnt) }}</td>
            <td>{{ fmt(stat.tstOdiIdx) }}</td>
          </tr>
          <tr>
            <td>TRT</td>
            <td>{{ fmtInt(stat.diffThdLge3Cnts) }}</td>
            <td>{{ fmt(stat.diffThdLge3Pr) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- SpO2 -->
      <table class="xs-table">
        <thead>
          <tr><th colspan="5" class="xs-section-header">SpO2</th></tr>
          <tr class="xs-col-header">
            <td>氧饱和度水平</td>
            <td>W</td>
            <td>S</td>
            <td>TRT</td>
            <td>--</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>最大血氧饱和度</td>
            <td>{{ fmt(stat.wMaxSpo2) }}</td>
            <td>{{ fmt(stat.sMaxSpo2) }}</td>
            <td>{{ fmt(stat.trtMaxSpo2) }}</td>
            <td>--</td>
          </tr>
          <tr>
            <td>平均血氧饱和度</td>
            <td>{{ fmt(stat.wMeanSpo2) }}</td>
            <td>{{ fmt(stat.sMeanSpo2) }}</td>
            <td>{{ fmt(stat.spo2avg) }}</td>
            <td>--</td>
          </tr>
          <tr>
            <td>最小血氧饱和度</td>
            <td>{{ fmt(stat.wMinSpo2) }}</td>
            <td>{{ fmt(stat.sMinSpo2) }}</td>
            <td>{{ fmt(stat.spo2min) }}</td>
            <td>--</td>
          </tr>
          <tr class="xs-col-header">
            <td colspan="4" style="text-align:center;">不同血氧占时间百分比</td>
            <td>总时长</td>
          </tr>
          <tr>
            <td>90% — 100%</td>
            <td>{{ fmtPct(stat.spo2WPct90) }}</td>
            <td>{{ fmtPct(stat.spo2SPct90) }}</td>
            <td>{{ fmtPct(stat.spo2TrtPct90) }}</td>
            <td>{{ stat.spo2Dur90Sec != null ? secondsToHMS(stat.spo2Dur90Sec) : '--' }}</td>
          </tr>
          <tr>
            <td>80% — 90%</td>
            <td>{{ fmtPct(stat.spo2WPct80) }}</td>
            <td>{{ fmtPct(stat.spo2SPct80) }}</td>
            <td>{{ fmtPct(spo280to90Pct) }}</td>
            <td>{{ spo280to90DurSec != null ? secondsToHMS(spo280to90DurSec) : '--' }}</td>
          </tr>
          <tr>
            <td>70% — 80%</td>
            <td>{{ fmtPct(stat.spo2WPct70) }}</td>
            <td>{{ fmtPct(stat.spo2SPct70) }}</td>
            <td>{{ fmtPct(stat.spo2Less80TimePercent) }}</td>
            <td>{{ stat.spo2Less80time != null ? secondsToHMS(stat.spo2Less80time) : '--' }}</td>
          </tr>
          <tr>
            <td>60% — 70%</td>
            <td>{{ fmtPct(stat.spo2WPct60) }}</td>
            <td>{{ fmtPct(stat.spo2SPct60) }}</td>
            <td>{{ fmtPct(stat.spo2Less70TimePercent) }}</td>
            <td>{{ stat.spo2Less70time != null ? secondsToHMS(stat.spo2Less70time) : '--' }}</td>
          </tr>
          <tr>
            <td>50% — 60%</td>
            <td>{{ fmtPct(stat.spo2WPct50) }}</td>
            <td>{{ fmtPct(stat.spo2SPct50) }}</td>
            <td>{{ fmtPct(stat.spo2TrtPct50) }}</td>
            <td>{{ stat.spo2Dur50Sec != null ? secondsToHMS(stat.spo2Dur50Sec) : '--' }}</td>
          </tr>
          <tr>
            <td>&lt; 50%</td>
            <td>{{ fmtPct(stat.spo2WPctLt50) }}</td>
            <td>{{ fmtPct(stat.spo2SPctLt50) }}</td>
            <td>{{ fmtPct(stat.spo2TrtPctLt50) }}</td>
            <td>{{ stat.spo2DurLt50Sec != null ? secondsToHMS(stat.spo2DurLt50Sec) : '--' }}</td>
          </tr>
        </tbody>
      </table>

      <!-- PR -->
      <table class="xs-table">
        <thead>
          <tr><th colspan="4" class="xs-section-header">PR</th></tr>
          <tr class="xs-col-header">
            <td>脉率水平</td>
            <td>W</td>
            <td>S</td>
            <td>TRT</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>最高脉率</td>
            <td>{{ fmtInt(stat.wMaxPr) }}</td>
            <td>{{ fmtInt(stat.sMaxPr) }}</td>
            <td>{{ fmtInt(stat.prMax) }}</td>
          </tr>
          <tr>
            <td>平均脉率</td>
            <td>{{ fmtInt(stat.wMeanPr) }}</td>
            <td>{{ fmtInt(stat.sMeanPr) }}</td>
            <td>{{ fmtInt(stat.prAvg) }}</td>
          </tr>
          <tr>
            <td>最低脉率</td>
            <td>{{ fmtInt(stat.wMinPr) }}</td>
            <td>{{ fmtInt(stat.sMinPr) }}</td>
            <td>{{ fmtInt(stat.prMin) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- <div style="page-break-after: always"></div>

      <section-header title="血氧统计"></section-header>
      <blood-oxygen :model="model"></blood-oxygen>
      <section-header title="脉率统计"></section-header>
      <pulse-statistics :model="model"></pulse-statistics> -->

      <div style="page-break-after: always"></div>

      <!-- <section-header title="监测数据"></section-header> -->
      <div class="xs-charts-section">
        <position-chart :timeTickArray="model.timeTickArray" :height="150"></position-chart>
        <line-chart
          title="血氧"
          :dataArray="model.bloodOxygenChartDataArray"
          :timeTickArray="model.timeTickArray"
          :height="150"
        ></line-chart>
        <!-- <line-chart
          title="睡眠呼吸趋势"
          :dataArray="model.breathChartDataArray"
          :timeTickArray="model.timeTickArray"
          :height="150"
        ></line-chart> -->
        <line-chart
          title="阻塞性呼吸暂停事件(秒)"
          :dataArray="model.breathEventChartDataArray"
          :timeTickArray="model.timeTickArray"
          :height="150"
        ></line-chart>
        <line-chart
          title="低通气事件(秒)"
          :dataArray="emptyChartData"
          :timeTickArray="model.timeTickArray"
          :height="150"
        ></line-chart>
        <!-- <combine-chart v-if="showBreathWaveChart" title="呼吸波趋势图" :model="model" /> -->
      </div>
      <div style="page-break-after: always"></div>
      <!-- <section-header title="评估建议"></section-header> -->
      <!-- <ahi-bar :ahi="model.ahi" :ahiTitle="model.ahiTitle" :degreeArray="model.ahiDegreeArray"></ahi-bar> -->
      <editor class="xs-editor-section" style="z-index: 0" :model="model" @onSaveClick="onEditorSaveClick"></editor>
      <!-- <report-bottom v-if="model.showReportBottom" :model="model" /> -->
    </div>
  </div>
</template>

<script>
import ReportTitle from "@c/report-detail/report-title.vue";
import SectionHeader from "@c/report-detail/section-header.vue";
import BloodOxygen from "@c/report-detail/blood-oxygen/blood-oxygen.vue";
import PulseStatistics from "@c/report-detail/pulse-statistics.vue";
import AhiBar from "@c/report-detail/ahi-bar.vue";
import Editor from "@c/report-detail/editor.vue";
import ReportBottom from "@c/report-detail/report-bottom.vue";
import LineChart from "@c/report-detail/sleep-chart/line-chart.vue";
import LineAreaChart from "@c/report-detail/sleep-chart/line-area-chart.vue";
import DoubleLineChart from "@c/report-detail/sleep-chart/double-line-chart.vue";
import DoubleLineAreaChart from "@c/report-detail/sleep-chart/double-line-area-chart.vue";
import CombineChart from "@c/report-detail/sleep-chart/combine-chart.vue";
import PositionChart from "@c/report-detail/sleep-chart/position-chart.vue";
import { DateTool } from "@js/date-tool.js";

export default {
  components: {
    ReportTitle,
    SectionHeader,
    BloodOxygen,
    PulseStatistics,
    AhiBar,
    Editor,
    ReportBottom,
    LineChart,
    LineAreaChart,
    DoubleLineChart,
    DoubleLineAreaChart,
    CombineChart,
    PositionChart,
  },

  props: {
    model: Object,
    patientBMI: String,
    showBreathWaveChart: Boolean,
  },

  computed: {
    stat() {
      return this.model?.model?.statistical || {};
    },
    userInfo() {
      return this.model?.model?.userInfo || {};
    },
    birthday() {
      return this.userInfo.birthday || null;
    },
    mobile() {
      return this.userInfo.mobile || null;
    },
    waistCircumference() {
      return this.userInfo.waistCircumference || null;
    },
    neckCircumference() {
      return this.userInfo.neckCircumference || null;
    },
    bloodPressureStr() {
      return this.model.bloodPressure || '--/--';
    },
    lightoffTimeStr() {
      const ts = this.stat.timeEnd;
      if (!ts) return '--';
      return DateTool.timestampToStr(ts, 'Y-M-D h:m:s') || '--';
    },
    lightonTimeStr() {
      const ts = this.stat.timeStart;
      if (!ts) return '--';
      return DateTool.timestampToStr(ts, 'Y-M-D h:m:s') || '--';
    },
    trtStr() {
      const off = this.stat.timeEnd;
      const on = this.stat.timeStart;
      if (!off || !on) return '--';
      const diffSeconds = off - on;
      if (diffSeconds <= 0) return '--';
      return this.secondsToHMS(diffSeconds);
    },
    tstStr() {
      const minutes = this.model.totalSleepMinutes;
      if (!minutes && minutes !== 0) return '--';
      return this.minutesToHMS(minutes);
    },
    tstHours() {
      return (this.model.totalSleepMinutes || 0) / 60;
    },
    longestTime() {
      return this.stat.longestTime != null ? this.stat.longestTime : this.stat.beMaxLen;
    },
    // 总呼吸暂停次数 = 阻塞 + 中枢 + 混合
    totalApneaCnt() {
      const o = this.stat.beoCnt ?? 0;
      const c = this.stat.becCnt ?? 0;
      const m = this.stat.bemCnt ?? 0;
      return o + c + m;
    },
    // 合计 = 总呼吸暂停 + 低通气
    totalEventCnt() {
      return this.totalApneaCnt + (this.stat.behCnt ?? 0);
    },
    emptyChartData() {
      const ticks = this.model.timeTickArray || [];
      if (ticks.length < 2) return [];
      return [
        { xValue: ticks[0], yValue: null },
        { xValue: ticks[ticks.length - 1], yValue: null },
      ];
    },
    spo280to90Pct() {
      const a = parseFloat(this.stat.spo2Less90TimePercent);
      const b = parseFloat(this.stat.spo2Less85TimePercent);
      if (isNaN(a) && isNaN(b)) return null;
      return (isNaN(a) ? 0 : a) + (isNaN(b) ? 0 : b);
    },
    spo280to90DurSec() {
      const a = parseFloat(this.stat.spo2Less85time);
      const b = parseFloat(this.stat.spo2Less90time);
      if (isNaN(a) && isNaN(b)) return null;
      return (isNaN(a) ? 0 : a) + (isNaN(b) ? 0 : b);
    },
    bmi() {
      if (this.model.initialPatientBMI != null && this.model.initialPatientBMI !== '') {
        return this.model.initialPatientBMI;
      }
      const h = parseFloat(this.model.initialPatientHeight);
      const w = parseFloat(this.model.initialPatientWeight);
      if (!h || !w || h <= 0) return null;
      return Math.round((w / ((h / 100) ** 2)) * 10) / 10;
    },
    snoreCntStr() {
      const cnt = this.stat.snoreNum != null ? this.stat.snoreNum : this.stat.snoreCnt;
      return cnt != null ? `${cnt}次` : '--次';
    },
    snoreIndexStr() {
      const cnt = this.stat.snoreNum != null ? this.stat.snoreNum : this.stat.snoreCnt;
      if (cnt == null || !this.tstHours) return '--次/小时';
      return `${this.roundOne(cnt / this.tstHours)}次/小时`;
    },
  },

  methods: {
    fmt(val, numeric = true) {
      if (val === null || val === undefined || val === '' || val === '--') return '--';
      if (!numeric) return val;
      const n = parseFloat(val);
      if (isNaN(n)) return val;
      return this.roundOne(n);
    },
    fmtInt(val) {
      if (val === null || val === undefined || val === '') return '--';
      const n = parseInt(val, 10);
      return isNaN(n) ? '--' : `${n}`;
    },
    fmtIndex(cnt) {
      if (cnt == null || cnt === '' || !this.tstHours) return '--';
      return this.roundOne(cnt / this.tstHours);
    },
    fmtUnit(val, unit) {
      if (val === null || val === undefined || val === '') return '--';
      return `${val}${unit}`;
    },
    roundOne(n) {
      return (Math.round(n * 10) / 10).toFixed(1);
    },
    fmtTwo(val) {
      if (val === null || val === undefined || val === '' || val === '--') return '--';
      const n = parseFloat(val);
      if (isNaN(n)) return '--';
      return (Math.round(n * 100) / 100).toFixed(2);
    },
    secondsToHMS(seconds) {
      const s = Math.floor(seconds);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    },
    minutesToHMS(minutes) {
      return this.secondsToHMS(Math.round(minutes * 60));
    },

    fmtPct(val) {
      if (val == null || val === '') return '--';
      const n = parseFloat(val);
      return isNaN(n) ? '--' : this.roundOne(n) + '%';
    },
    fmtPosIndex(cnt, timeSeconds) {
      if (cnt == null || cnt === '' || timeSeconds == null || timeSeconds === 0) return '--';
      return this.roundOne(cnt / (timeSeconds / 3600));
    },
    posTotalApnea(o, c, m) {
      if (o == null && c == null && m == null) return null;
      return (o ?? 0) + (c ?? 0) + (m ?? 0);
    },
    posTotal(o, c, m, h, cs) {
      if (o == null && c == null && m == null && h == null && cs == null) return null;
      return (o ?? 0) + (c ?? 0) + (m ?? 0) + (h ?? 0) + (cs ?? 0);
    },

    onDownloadPDFClick() { this.$emit('onDownloadPDFClick'); },
    onDownloadPNGClick() { this.$emit('onDownloadPNGClick'); },
    onPrintClick() { this.$emit('onPrintClick'); },
    onShareClick() { this.$emit('onShareClick'); },
    onSaveClick() { this.$emit('onSaveClick'); },
    onInputInput(title, value) { this.$emit('onInputInput', title, value); },
    onEditorSaveClick(html) { this.$emit('onEditorSaveClick', html); },
  },
};
</script>

<style lang="scss" scoped>
.report-xiaoshan {
  width: 100%;

  &-container {
    width: rem(750);
    margin: 0 auto;
    padding: 0;
  }
}

.xs-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
  font-size: 13px;

  th,
  td {
    border: 1px solid #ccc;
    padding: 5px 8px;
    text-align: left;
  }

  .xs-section-header {
    background-color: #d9d9d9;
    font-weight: bold;
    font-size: 14px;
    padding: 6px 8px;
  }

  .xs-label {
    background-color: #f5f5f5;
    white-space: nowrap;
  }

  tr.xs-col-header td {
    background-color: #f5f5f5;
    font-weight: bold;
    text-align: center;
  }

  td:not(.xs-label):not(:first-child) {
    text-align: center;
  }
}

// 打印页眉：屏幕上隐藏，打印时固定在每页顶部
.xs-print-header {
  display: none;

  @media print {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: white;
    text-align: center;
    padding: 10px 0 8px;
    z-index: 999;
  }

  &-title {
    font-size: rem(22);
    font-weight: bold;
    line-height: rem(32);
  }

  &-subtitle {
    font-size: rem(18);
    line-height: rem(28);
  }
}

// 屏幕上正常显示，打印时隐藏原始标题栏
.xs-screen-only {
  @media print {
    display: none;
  }
}

// 打印时容器顶部留出页眉高度
@media print {
  .report-xiaoshan-container {
    padding-top: 120px;
  }

  // 第三页：图表区域顶部留出页眉高度（图表紧跟 page-break 后，会出现在新页顶部）
  .xs-charts-section {
    margin-top: 80px;
  }

  // 第四页：编辑器区域顶部留出页眉高度
  .xs-editor-section {
    // margin-top: 150px;
  }
}
</style>
